import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  KeyboardAvoidingView,
  NativeModules,
  Platform,
  Pressable,
  ScrollView,
  View,
} from "react-native";
import { launchCamera, launchImageLibrary } from "@utils/imagePickerCompat";
import { useSelector } from "react-redux";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import checkPermissions from "@screens/SupportTree/components/Form/Images/checkPermissions";
import toast from "@utils/toast";
import getTokenZ from "@utils/getTokenZ";
import t from "@constants/localization";
import Text from "@components/CustomText";
import FetchCMSEntity from "@components/CMS/FetchCMSEntity";
import useCMSEvents from "@hooks/useCMSEvents";
import {
  deletePrivateImage,
  getLeaderKycInfo,
  submitLeaderKycV2,
  uploadPrivateImage,
} from "../../../services/api";
import {
  getJsonData,
  saveJsonData,
  removeJson,
  kycDraftKey,
  clearKycDrafts,
} from "../../../utils/commonFunctions";
import {
  isKycFaceCaptureAvailable,
  captureKycFace,
  isCapturedFace,
  deleteCapturedFace,
} from "../../../utils/kycFaceCapture";
import AadharSection from "./AadharSection";
import KYCComment from "./Comment";
import PANSection from "./PanSection";
import ProcessingModal from "./ProcessingModal";
import SuccessModal from "./SuccessModal";
import KYCStatus from "./Status";
import UserPhotoSection from "./UserPhotoSection";
import analytics from "../../../services/analytics";
import { CL_KYC } from "../../../constants/analyticsEventsCT";
import testIds from "../../../constants/testIds";
import { kycStyles as s } from "./styles";

const PATH = "screens/KYCSetup/index";

const PAN_NUMBER_REGEX = /[A-Z]{5}[0-9]{4}[A-Z]{1}/;
const AADHAR_NUMBER_REGEX = /[0-9]{12}/;
const KYC_CMS_KEY = "KYC_CMS_KEY";

const KYCSetup = (props) => {
  const { route, navigation } = props;

  const goBack = () => navigation.goBack();

  const [userPhoto, setUserPhoto] = useState({ source: "", filename: "" });
  const [disableUserPhotoSection, setDisableUserPhotoSection] = useState(false);
  const [verifyUserPhotoSection, setVerifyUserPhotoSection] = useState(false);

  const [aadharNumber, setAadharNumber] = useState("");
  const [aadharFront, setAadharFront] = useState({ source: "", filename: "" });
  const [aadharBack, setAadharBack] = useState({ source: "", filename: "" });
  const [disableAadharSection, setDisableAadharSection] = useState(false);
  const [verifyAadharSection, setVerifyAadharSection] = useState(false);

  const [panNumber, setPanNumber] = useState("");
  const [panPhoto, setPanPhoto] = useState("");
  const [disablePanSection, setDisablePanSection] = useState(false);
  const [verifyPanSection, setVerifyPanSection] = useState(false);

  const [userKycData, setUserKycData] = useState([]);
  const [kycData, setKycData] = useState([]);
  const [kycStatus, setKycStatus] = useState("NONE");
  const [refresh, toggleRefresh] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isAadharPanLinked, setIsAadharPanLinked] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetched, setFetched] = useState(false);

  const appSettings =
    useSelector((state) => state.configReducer.appSettings) || {};
  const { cmsPoweredEntities = {} } = appSettings || {};

  // Draft persistence is SCOPED to the logged-in user so one user's in-progress
  // KYC never restores into another user's session (see the restore effect).
  const kycScopeId = useSelector(
    ({ auth }) => auth?.user?.cp_db_id || auth?.user?.tl_id || null,
  );

  // Auth token so the private uploaded selfie URL renders in <Image>
  // (get-private-file needs the DB's session token; a plain <Image> sends none).
  const [imgAuthToken, setImgAuthToken] = useState(null);
  useEffect(() => {
    getTokenZ()
      .then(setImgAuthToken)
      .catch(() => {});
  }, []);

  // Exempted/impersonated users (face_capture_exempted_cpdbs) use the plain
  // camera for the selfie. Carried on the dedicated X-Face-Capture-Exempt
  // boolean header — NOT the old X-Face-Capture config header, which old-build
  // interceptors dispatch into Redux and which used to launch the auto modal.
  const [faceCaptureExempt, setFaceCaptureExempt] = useState(false);

  useEffect(() => {
    setShowProcessingModal(true);
    getLeaderKycInfo()
      .then((res) => {
        const { data } = res;
        setFaceCaptureExempt(res?.headers?.["x-face-capture-exempt"] === "1");
        setShowProcessingModal(false);
        const dataObj = data.reduce(
          (acc, curr) => ({ ...acc, [curr.kyc_item_name]: curr }),
          {},
        );

        for (const i in dataObj) {
          delete dataObj[i].cl_id;
          delete dataObj[i].verified_at;
          delete dataObj[i].created_at;
          delete dataObj[i].submitted_at;
          delete dataObj[i].verified_by;
          delete dataObj[i].rejected_by;
          delete dataObj[i].rejected_at;
        }

        setUserKycData(data);
        setKycData(dataObj);
        setFetched(true);
        analytics.trackEvent(CL_KYC.KYC_LANDING);
      })
      .catch((err) => {
        console.warn(err);
        setShowProcessingModal(false);
        navigation.navigate("Home");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  // Restore an in-progress draft for a brand-new KYC (no server record yet).
  // Keys are SCOPED to kycScopeId, so a different user never restores this
  // user's draft — the previous global keys leaked across logout. Drafts are
  // cleared on logout (WalletDrawer) and on successful submit (onSubmit).
  useEffect(() => {
    if (kycStatus !== "NONE" || !fetched || !kycScopeId) return;
    const docs = {
      kyc_user_photo: setUserPhoto,
      kyc_aadhar_front: setAadharFront,
      kyc_aadhar_back: setAadharBack,
      kyc_pan_image: setPanPhoto,
    };
    const numbers = {
      kyc_aadhar_number: setAadharNumber,
      kyc_pan_number: setPanNumber,
    };
    Object.keys(docs).forEach((base) => {
      getJsonData(kycDraftKey(base, kycScopeId)).then((value) => {
        if (value) docs[base](value);
      });
    });
    Object.keys(numbers).forEach((base) => {
      getJsonData(kycDraftKey(base, kycScopeId)).then((value) => {
        if (value) numbers[base](value);
      });
    });
  }, [kycStatus, fetched, kycScopeId]);

  useEffect(() => {
    if (kycData && typeof kycData === "object" && Object.keys(kycData).length) {
      const parentStatus = kycData?.user_photo?.kyc_status;
      const isEnforcing =
        kycData?.user_photo?.review_source === "CP_DB_ENFORCING" &&
        !kycData?.user_photo?.admin_action_at;
      // Only a document that is ACTUALLY rejected needs a re-upload. An enforcing
      // row can read PARTIALLY_VERIFIED with NOTHING rejected: the selfie was
      // rejected on partner web and the CP has ALREADY re-uploaded it (now
      // PROCESSING), or a Decentro re-check transiently folded the parent to
      // PARTIALLY_VERIFIED while every item is VERIFIED/PROCESSING.
      const anyItemRejected = Object.values(kycData).some(
        (item) => item?.kyc_item_status === "REJECTED",
      );
      let effectiveStatus = parentStatus;
      if (isEnforcing) {
        if (anyItemRejected) {
          // Something genuinely failed → PARTIALLY_VERIFIED unlocks exactly the
          // rejected section (with its reason) for re-upload. Also covers rows
          // whose parent still reads VERIFIED while an item is REJECTED (deploy
          // lag / cron folding).
          effectiveStatus = "PARTIALLY_VERIFIED";
        } else if (
          parentStatus === "VERIFIED" ||
          parentStatus === "PARTIALLY_VERIFIED"
        ) {
          // Nothing to re-upload → this is a REVIEW state, not a "re-upload the
          // failed documents" state. Fold to VERIFIED so the review banner
          // (bannerStatus → UNDER_PARTNER_REVIEW / UNDER_ADMIN_REVIEW) and the
          // locked tiles render, instead of the misleading partial banner + a
          // green tick on a selfie that is actually awaiting re-review.
          effectiveStatus = "VERIFIED";
        }
      }
      setKycStatus(effectiveStatus);
      setIsAadharPanLinked(
        kycData?.is_pan_linked_to_aadhar?.kyc_item_value === "true",
      );
    }
  }, [kycData]);

  const mapKycDataToState = useMemo(
    () =>
      (kycItemName, setItem, image = false) => {
        const value = kycData[kycItemName]?.kyc_item_value;
        setItem((i) => (image ? { ...i, source: value } : value));
      },
    [kycData],
  );

  useEffect(() => {
    switch (kycStatus) {
      case "VERIFIED":
      case "PROCESSING": {
        mapKycDataToState("user_photo", setUserPhoto, true);
        setDisableUserPhotoSection(true);

        mapKycDataToState("aadhar_number", setAadharNumber);
        mapKycDataToState("aadhar_front", setAadharFront, true);
        mapKycDataToState("aadhar_back", setAadharBack, true);
        setDisableAadharSection(true);

        mapKycDataToState("pan_number", setPanNumber);
        mapKycDataToState("pan_image", setPanPhoto, true);
        setDisablePanSection(true);
        break;
      }
      case "REJECTED": {
        // Pre-fill the previously submitted details from get-kyc so the user can
        // see what they uploaded (alongside the rejection reasons) and edit/
        // resubmit. Explicitly ENABLE every section — including Aadhaar and PAN
        // (number inputs + document tiles) — so they can be fixed.
        setDisableUserPhotoSection(false);
        setDisableAadharSection(false);
        setDisablePanSection(false);
        mapKycDataToState("user_photo", setUserPhoto, true);
        mapKycDataToState("aadhar_number", setAadharNumber);
        mapKycDataToState("aadhar_front", setAadharFront, true);
        mapKycDataToState("aadhar_back", setAadharBack, true);
        mapKycDataToState("pan_number", setPanNumber);
        mapKycDataToState("pan_image", setPanPhoto, true);
        break;
      }
      case "PARTIALLY_VERIFIED": {
        const rejectedDocuments = Object.keys(kycData).reduce((acc, curr) => {
          if (kycData[curr].kyc_item_status === "REJECTED") {
            return acc.concat(kycData[curr]?.kyc_item_name);
          }
          return acc;
        }, []);

        const verifiedDocuments = Object.keys(kycData).reduce((acc, curr) => {
          if (kycData[curr].kyc_item_status === "VERIFIED") {
            return acc.concat(kycData[curr]?.kyc_item_name);
          }
          return acc;
        }, []);

        // A REJECTED section must ALWAYS be editable and prefilled so the CP can
        // fix it — EXPLICITLY (never rely on the default), because the parent can
        // be PROCESSING/VERIFIED while an item is REJECTED (e.g. admin rejects the
        // whole KYC, then a partial re-upload flips the parent to PROCESSING while
        // the numbers stay REJECTED). Leaving it to the default left the field
        // locked after a re-fetch.
        if (!rejectedDocuments.includes("user_photo")) {
          mapKycDataToState("user_photo", setUserPhoto, true);
          setDisableUserPhotoSection(true);
          if (verifiedDocuments.includes("user_photo")) {
            setVerifyUserPhotoSection(true);
          }
        } else {
          // Editable + prefilled. The rejection reason itself is shown ONCE at
          // the top (rejectionReasons), not per-section.
          setDisableUserPhotoSection(false);
          mapKycDataToState("user_photo", setUserPhoto, true);
        }

        if (
          !rejectedDocuments.includes("aadhar_number") &&
          !rejectedDocuments.includes("aadhar_front") &&
          !rejectedDocuments.includes("aadhar_back")
        ) {
          mapKycDataToState("aadhar_number", setAadharNumber);
          mapKycDataToState("aadhar_front", setAadharFront, true);
          mapKycDataToState("aadhar_back", setAadharBack, true);
          setDisableAadharSection(true);
          if (
            verifiedDocuments.includes("aadhar_number") &&
            verifiedDocuments.includes("aadhar_front") &&
            verifiedDocuments.includes("aadhar_back")
          ) {
            setVerifyAadharSection(true);
          }
        } else {
          setDisableAadharSection(false);
          mapKycDataToState("aadhar_number", setAadharNumber);
          mapKycDataToState("aadhar_front", setAadharFront, true);
          mapKycDataToState("aadhar_back", setAadharBack, true);
        }

        if (
          !rejectedDocuments.includes("pan_number") &&
          !rejectedDocuments.includes("pan_image")
        ) {
          mapKycDataToState("pan_number", setPanNumber);
          mapKycDataToState("pan_image", setPanPhoto, true);
          setDisablePanSection(true);
          if (
            verifiedDocuments.includes("pan_number") &&
            verifiedDocuments.includes("pan_image")
          ) {
            setVerifyPanSection(true);
          }
        } else {
          setDisablePanSection(false);
          mapKycDataToState("pan_number", setPanNumber);
          mapKycDataToState("pan_image", setPanPhoto, true);
        }
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycStatus]);

  // User photo (selfie): capture through the face-gated flow so every KYC selfie
  // is guaranteed to contain a real, in-frame face, then run the SAME upload as
  // the camera path (onImageSelect → /upload-private-image). Falls back to the
  // camera picker on binaries without the native capture-and-return method, or
  // if the capture errors. Aadhaar/PAN tiles are unchanged (allowGallery = true).
  const uploadUserPhoto = async () => {
    // Face-gated capture whenever the binary supports it. Plain camera for
    // exempted/impersonated users (X-Face-Capture-Exempt header) and for older
    // binaries without the native method.
    if (!faceCaptureExempt && isKycFaceCaptureAvailable()) {
      try {
        const result = await captureKycFace();
        // Backed out / no photo → no-op (leave the tile + its file as-is).
        if (!isCapturedFace(result)) return;
        await onImageSelect(
          "kyc_user_photo",
          setUserPhoto,
        )({
          uri: result.uri,
          type: "image/jpeg",
          fileName: `kyc_${Date.now()}.jpg`,
        });
        // Uploaded → preview from the uploaded URL and drop the local capture
        // file immediately (clear localUri so the tile renders `source`).
        setUserPhoto((p) => (p?.source ? { ...p, localUri: "" } : p));
        deleteCapturedFace(result.uri);
        return;
      } catch (e) {
        // Another capture session is already on screen (e.g. a LOGIN/DELIVERIES
        // auto modal) — do NOT silently swap to the plain camera; the user can
        // simply tap again. Only hard failures fall back to the camera.
        if (e?.code === "E_ALREADY_RUNNING") return;
        console.log("kyc face capture failed; falling back to camera", e);
      }
    }
    uploadPhoto(setUserPhoto, "kyc_user_photo", false);
  };

  const uploadAadharFront = () => {
    uploadPhoto(setAadharFront, "kyc_aadhar_front", true);
  };

  const uploadAadharBack = () => {
    uploadPhoto(setAadharBack, "kyc_aadhar_back", true);
  };

  const uploadPanPhoto = () => {
    uploadPhoto(setPanPhoto, "kyc_pan_image", true);
  };

  const removeUserPhoto = () => {
    removePhoto(userPhoto.filename, setUserPhoto, "kyc_user_photo");
  };

  const removeAadharFront = () => {
    removePhoto(aadharFront.filename, setAadharFront, "kyc_aadhar_front");
  };

  const removeAadharBack = () => {
    removePhoto(aadharBack.filename, setAadharBack, "kyc_aadhar_back");
  };

  const removePanPhoto = () => {
    removePhoto(panPhoto.filename, setPanPhoto, "kyc_pan_image");
  };

  const onImageSelect = (localImgName, setPhotoState) => async (response) => {
    if (!response || response.didCancel) {
      return;
    }
    if (response.error) {
      console.warn(response.error);
      return;
    }
    if (!response.uri) {
      return;
    }
    // Derive extension from type OR filename; never crash on a missing type.
    const ext = (
      response.type?.split("/")[1] ||
      response.fileName?.split(".").pop() ||
      ""
    ).toLowerCase();
    const allowedFileFormat = ["jpg", "jpeg", "png"];
    if (ext && !allowedFileFormat.includes(ext)) {
      // eslint-disable-next-line no-alert
      alert(t(`${PATH}/unsupportedFileFormat`));
      return;
    }
    // Show the captured image immediately (local URI) — visible even while the
    // upload is in flight and even if the upload fails.
    setPhotoState((prev) => ({
      ...(prev && typeof prev === "object" ? prev : {}),
      localUri: response.uri,
    }));
    setShowProcessingModal(true);
    const data = new FormData();
    data.append("image", {
      uri: response.uri,
      type: response.type || "image/jpeg",
      name: response.fileName || `kyc_${Date.now()}.jpg`,
    });
    try {
      const { data: uploadData } = await uploadPrivateImage(data);
      if (uploadData?.url) {
        const remoteFilename = uploadData.url.split("/").reverse()[0];
        // Persist the uploaded image as a user-scoped draft so it survives an
        // app restart before submit (restored by the effect above).
        const draftK = kycDraftKey(localImgName, kycScopeId);
        if (draftK) {
          await saveJsonData(draftK, {
            source: uploadData.url,
            filename: remoteFilename,
          });
        }
        setPhotoState((prev) => ({
          ...(prev && typeof prev === "object" ? prev : {}),
          source: uploadData.url,
          filename: remoteFilename,
          localUri: response.uri,
        }));
      } else {
        // Backend returns { error } with HTTP 200 on failure (e.g. file too
        // large). Surface it and clear the tile so it isn't a silent no-op
        // that leaves submit disabled with the image still on screen.
        toast.showLongToast(
          uploadData?.message ||
            uploadData?.error ||
            "फोटो अपलोड नहीं हो पाई, कृपया दोबारा कोशिश करें",
        );
        setPhotoState({ source: "", filename: "", localUri: "" });
      }
    } catch (e) {
      console.log(e);
      toast.showLongToast("फोटो अपलोड नहीं हो पाई, कृपया दोबारा कोशिश करें");
      setPhotoState({ source: "", filename: "", localUri: "" });
    } finally {
      setShowProcessingModal(false);
    }
  };

  const uploadPhoto = async (
    setPhotoState,
    localImgName,
    allowGallery = false,
  ) => {
    try {
      await checkPermissions();
    } catch (e) {
      const { AppSettingsIntentModule } = NativeModules;
      Alert.alert(
        t(`${PATH}/permissionsDenied`),
        t(`${PATH}/askForPermissionMsg`),
        [
          {
            text: t(`${PATH}/tryAgain`),
            onPress: () =>
              uploadPhoto(setPhotoState, localImgName, allowGallery),
          },
          {
            text: t(`${PATH}/goToSettings`),
            onPress: () => AppSettingsIntentModule.openAppSettings(),
          },
        ],
      );

      console.warn(e);
      setShowProcessingModal(false);
      return;
    }
    const pickerOptions = {
      mediaType: "photo",
      maxWidth: 1600,
      maxHeight: 2048,
      quality: 0.7,
    };
    const openCamera = () =>
      launchCamera(pickerOptions, onImageSelect(localImgName, setPhotoState));
    try {
      // Gallery is only offered for the Aadhaar/PAN document tiles; the user
      // photo (and every other upload in the app) is camera-only.
      if (!allowGallery) {
        openCamera();
        return;
      }
      Alert.alert(
        t(`${PATH}/uploadPhoto`),
        t(`${PATH}/youWantToPickPhotoFrom`),
        [
          { text: t(`${PATH}/fromCamera`), onPress: openCamera },
          {
            text: t(`${PATH}/fromGallery`),
            onPress: () =>
              launchImageLibrary(
                pickerOptions,
                onImageSelect(localImgName, setPhotoState),
              ),
          },
        ],
        { cancelable: true },
      );
    } catch (e) {
      console.warn(e);
      setShowProcessingModal(false);
    }
  };

  const removePhoto = async (filename, setPhotoState, localImgName) => {
    // Clear the tile regardless of upload state (also clears the local preview);
    // only hit the delete API when there's an uploaded file to remove.
    if (filename) {
      try {
        await deletePrivateImage(filename);
      } catch (e) {
        console.log(e);
      }
    }
    setPhotoState({ source: "", filename: "", localUri: "" });
    const draftK = kycDraftKey(localImgName, kycScopeId);
    if (draftK) removeJson(draftK);
  };

  const onSubmit = async () => {
    if (isSubmitting) {
      return;
    }
    // Diff-based resubmit (v2): send only the items whose value changed vs the
    // get-kyc snapshot, so only edited documents are re-verified. The backend
    // resets each changed item to PROCESSING regardless of its prior status.
    const originalValue = (name) => kycData?.[name]?.kyc_item_value ?? "";
    const fields = [
      { name: "user_photo", value: userPhoto.source },
      { name: "aadhar_number", value: aadharNumber },
      { name: "aadhar_front", value: aadharFront.source },
      { name: "aadhar_back", value: aadharBack.source },
      { name: "pan_number", value: panNumber },
      { name: "pan_image", value: panPhoto.source },
      // Delivery address isn't collected here; mirror the legacy "same address
      // = yes" contract — only sent when it's new (i.e. differs from stored).
      { name: "is_address_same_as_aadhar", value: "yes" },
    ];
    const kyc_items = fields
      .filter((f) => {
        const changed = String(f.value ?? "") !== String(originalValue(f.name));
        // Also re-send a currently-REJECTED item even if its value is unchanged.
        // An admin whole-KYC rejection marks correct fields (e.g. the Aadhaar/PAN
        // number) REJECTED too, and re-typing the same value yields no diff. The
        // backend resets any re-submitted REJECTED item back to PROCESSING so it
        // re-enters verification (and re-runs the PAN–Aadhaar linkage check);
        // without this it would stay REJECTED and the KYC could never complete.
        // Guard on a non-empty value so a blank field is never submitted.
        const rejectedNeedsResend =
          kycData?.[f.name]?.kyc_item_status === "REJECTED" &&
          String(f.value ?? "").length > 0;
        return changed || rejectedNeedsResend;
      })
      .map((f) => ({ kyc_item_name: f.name, kyc_item_value: f.value }));

    if (kyc_items.length === 0) {
      toast.showLongToast("कोई बदलाव नहीं किया गया");
      return;
    }

    setIsSubmitting(true);
    try {
      await submitLeaderKycV2({ kyc_items });
      // Submitted → the server is now the source of truth; drop the local draft.
      clearKycDrafts(kycScopeId);
      toggleRefresh((refreshToggler) => !refreshToggler);
      setShowSuccessModal(true);
      analytics.trackEvent(CL_KYC.KYC_SUBMIT);
    } catch (e) {
      // A rejected resubmit (e.g. 409 AADHAAR_ALREADY_IN_USE) must never fall
      // through to the success modal or clear the draft — the api-utils
      // interceptor has already surfaced the server's message. Swallow here so
      // it isn't an unhandled rejection and the CP can fix + retry.
      console.log("[kyc-v2] submit rejected", e?.response?.status);
    } finally {
      setIsSubmitting(false);
    }
  };

  const trackEvent = useCMSEvents("kyclanding");

  const submitDisabled = useMemo(() => {
    switch (kycStatus) {
      case "NONE":
      case "PARTIALLY_VERIFIED":
      case "REJECTED": {
        if (
          !userPhoto.source ||
          !AADHAR_NUMBER_REGEX.test(aadharNumber) ||
          !aadharFront.source ||
          !aadharBack.source ||
          !PAN_NUMBER_REGEX.test(panNumber) ||
          !panPhoto.source
        ) {
          return true;
        }
        return false;
      }
      default: {
        return false;
      }
    }
  }, [
    aadharBack.source,
    aadharFront.source,
    aadharNumber,
    panNumber,
    panPhoto.source,
    userPhoto.source,
    kycStatus,
  ]);

  const showSubmit =
    kycStatus === "NONE" ||
    kycStatus === "REJECTED" ||
    (kycStatus === "PARTIALLY_VERIFIED" &&
      userKycData.find((item) => item.kyc_item_status === "REJECTED"));

  // Distinct rejection reasons across ALL documents, shown ONCE at the top so
  // the CP sees every unique reason exactly once. A whole-KYC rejection stamps
  // the SAME reason on many items, so the old per-section comments repeated it
  // 2–4× down the page; the reasons are now surfaced only here (the sections no
  // longer render their own rejectionComment). Covers REJECTED and the
  // enforcing PARTIALLY_VERIFIED state.
  const rejectionReasons = useMemo(() => {
    if (!["REJECTED", "PARTIALLY_VERIFIED"].includes(kycStatus)) return [];
    if (!kycData || typeof kycData !== "object") return [];
    // ONLY collect from items that are CURRENTLY rejected. Verifying an item
    // doesn't clear its old kyc_item_rejection_comment, so a re-verified doc can
    // still carry a stale reason (e.g. a prior admin FRAUD_DB) — reading it
    // regardless of status leaked that stale strip alongside the real one.
    const reasons = Object.keys(kycData)
      .filter((name) => kycData[name]?.kyc_item_status === "REJECTED")
      .map((name) => kycData[name]?.kyc_item_rejection_comment)
      .filter((c) => typeof c === "string" && c.trim().length);
    return Array.from(new Set(reasons.map((c) => c.trim())));
  }, [kycData, kycStatus]);

  // OLD-DB manual review (enforcing): parent kyc_status = VERIFIED only means
  // Decentro passed — the KYC is DONE only once the admin approves. Until then
  // show the review stage instead of "Congratulations": CP review before
  // partner_action_at, admin review after. get-kyc returns the review columns
  // (review_source / partner_action_at / admin_action_at) on every item row;
  // user_photo always exists on this flow. Non-enforcing rows are unaffected.
  const reviewMeta = kycData?.user_photo;
  const bannerStatus = useMemo(() => {
    if (
      kycStatus === "VERIFIED" &&
      reviewMeta?.review_source === "CP_DB_ENFORCING" &&
      !reviewMeta?.admin_action_at
    ) {
      return reviewMeta?.partner_action_at
        ? "UNDER_ADMIN_REVIEW"
        : "UNDER_PARTNER_REVIEW";
    }
    return kycStatus;
  }, [kycStatus, reviewMeta]);

  return (
    <View style={s.container} testID={testIds.KYC_container}>
      <HeaderWithBackAndTitle
        headertitle={t(`${PATH}/completeKYC`)}
        navigation={navigation}
        route={route}
        hideShadow
      />
      {/* Keyboard-aware wrapper: without it the soft keyboard overlapped the
          Aadhaar/PAN inputs at the bottom of the page (no windowSoftInputMode
          set on MainActivity). Same behavior pattern as the rest of the app. */}
      <KeyboardAvoidingView
        style={s.flexFill}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <ScrollView
          testID={testIds.kyc_scroll}
          contentContainerStyle={s.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {cmsPoweredEntities &&
          typeof cmsPoweredEntities === "object" &&
          KYC_CMS_KEY in cmsPoweredEntities ? (
            <FetchCMSEntity
              trackEvent={trackEvent}
              id={cmsPoweredEntities[KYC_CMS_KEY]}
              type="PAGE"
              isPaddingVertical={false}
            />
          ) : null}
          <KYCStatus
            statusText={bannerStatus}
            isAadharPanLinked={isAadharPanLinked}
          />
          {/* Rejection reasons come SOLELY from currently-REJECTED items
              (rejectionReasons). The parent kyc_comment strip was dropped — it
              wasn't refreshed on later transitions, so a superseded reason
              (e.g. a prior admin FRAUD_DB) lingered next to the real one. */}
          {rejectionReasons.map((reason) => (
            <KYCComment key={reason} kycComment={reason} />
          ))}
          <UserPhotoSection
            {...{
              userPhoto,
              uploadUserPhoto,
              removeUserPhoto,
              disableUserPhotoSection,
              kycStatus,
            }}
            verified={verifyUserPhotoSection}
            imageHeaders={imgAuthToken ? { Authorization: imgAuthToken } : null}
          />
          <AadharSection
            {...{
              aadharBack,
              aadharFront,
              aadharNumber,
              setAadharNumber,
              uploadAadharFront,
              uploadAadharBack,
              removeAadharFront,
              removeAadharBack,
              disableAadharSection,
            }}
            testAadharID={testIds.enter_aadhar}
            verified={verifyAadharSection}
            draftKey={kycDraftKey("kyc_aadhar_number", kycScopeId)}
            imageHeaders={imgAuthToken ? { Authorization: imgAuthToken } : null}
          />
          <PANSection
            testPANid={testIds.enter_PAN}
            {...{
              panNumber,
              setPanNumber,
              panPhoto,
              uploadPanPhoto,
              removePanPhoto,
              disablePanSection,
            }}
            verified={verifyPanSection}
            draftKey={kycDraftKey("kyc_pan_number", kycScopeId)}
            imageHeaders={imgAuthToken ? { Authorization: imgAuthToken } : null}
          />
        </ScrollView>
        {showSubmit ? (
          <View style={s.bottomBar}>
            <Pressable
              style={[
                s.primaryButton,
                submitDisabled && s.primaryButtonDisabled,
              ]}
              disabled={submitDisabled || isSubmitting}
              onPress={onSubmit}
              accessibilityRole="button"
              accessibilityLabel={t(`${PATH}/submit`)}
            >
              <Text variant="title_16" style={s.primaryButtonText}>
                {t(`${PATH}/submit`)}
              </Text>
            </Pressable>
          </View>
        ) : null}
      </KeyboardAvoidingView>
      <ProcessingModal showProcessingModal={showProcessingModal} />
      <SuccessModal
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowProcessingModal}
        goBack={goBack}
      />
    </View>
  );
};

export default KYCSetup;
